/**
 * Life4Earth V2 — Bundles tab on the Admin Role Catalogue page (DEV-1214 iter 1).
 *
 * Two-pane layout rendered when the user selects the "Bundles" tab on
 * AdminRoleRhythms. Read-only in iter 1:
 *   * Left pane:  searchable list of Role Bundles (active by default).
 *   * Right pane: selected bundle detail + its constituent roles.
 *
 * Create / edit / retire / role-attach affordances ship in iters 2-4.
 *
 * Backend mapping:
 *   GET /api/admin/role-bundles
 *   GET /api/admin/role-bundles/{guid}/roles
 *
 * English-only — system admin UI per CLAUDE.md.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  attachRoleBundleToDefinition,
  fetchAllRoleBundlesAdmin,
  fetchRoleBundleRoles,
  retireRoleBundle,
  setRoleDefinitionSortOrder,
  updateRoleBundle,
  type RoleBundleResponse,
  type RoleBundleRoleSummaryResponse,
} from "../../api/roleBundles";
import AddRoleToBundleTray from "./AddRoleToBundleTray";
import BundleEditTray from "./BundleEditTray";
import styles from "./AdminRoleRhythms.module.css";

export default function BundlesTabContent() {
  const [bundles, setBundles] = useState<RoleBundleResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGuid, setSelectedGuid] = useState<string | null>(null);

  // iter 4 — toggle for showing retired bundles in the list.
  const [showRetired, setShowRetired] = useState(false);

  // Lazy-fetched roles for the selected bundle.
  const [bundleRoles, setBundleRoles] = useState<RoleBundleRoleSummaryResponse[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // iter 2 — Create/Edit tray state. Two modes share one component.
  const [trayMode, setTrayMode] = useState<"create" | "edit" | null>(null);

  // iter 3 — Add-roles-to-bundle picker tray.
  const [addRolesTrayOpen, setAddRolesTrayOpen] = useState(false);

  // ── Initial list load ─────────────────────────────────────

  const loadBundles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAllRoleBundlesAdmin();
      setBundles(result);
    } catch (err: any) {
      setError(err?.message || "Failed to load Role Bundles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBundles();
  }, [loadBundles]);

  // Reusable refetch — used by save / attach / detach handlers.
  // MUST be declared after loadBundles so handleDetachRole's deps array
  // can reference both without hitting a temporal dead zone (the bug
  // Skip caught on 2026-06-04: hooks at module top-level evaluate deps
  // synchronously during render, so any reference to a later-declared
  // const throws ReferenceError before its initializer runs).
  const refreshBundleRoles = useCallback(async () => {
    if (!selectedGuid) return;
    setRolesLoading(true);
    try {
      const roles = await fetchRoleBundleRoles(selectedGuid);
      setBundleRoles(roles);
    } catch {
      setBundleRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, [selectedGuid]);

  // Detach a single role from this bundle. Called by the BundleDetail
  // role row's × button (iter 3).
  const handleDetachRole = useCallback(async (definitionGuid: string) => {
    try {
      await attachRoleBundleToDefinition(definitionGuid, null);
      // Refresh the bundle list (so RoleCount on the left pane updates)
      // AND the per-bundle role list (so the row disappears).
      await loadBundles();
      await refreshBundleRoles();
    } catch (err) {
      // Surface in the page-level error banner; reusing the existing error state.
      setError((err as any)?.message || "Failed to detach role.");
    }
  }, [loadBundles, refreshBundleRoles]);

  // iter 3.5 — reorder roles within the current bundle. Mirrors the
  // AdminRoleRhythms task-reorder pattern: caller passes the new
  // role-order array; we compute which roles changed position, fire N
  // sequential setRoleDefinitionSortOrder PUTs, then refresh once.
  // Sequential (not parallel) so a partial failure can be attributed to
  // a specific row; the role count is small (~tens), so the latency is
  // negligible.
  const handleReorderRoles = useCallback(
    async (newOrder: RoleBundleRoleSummaryResponse[]) => {
      // Renumber 0..N-1 within the bundle. We update every row whose
      // new index differs from its current sort_order — usually most of
      // them after a drag.
      const updates = newOrder
        .map((role, idx) => ({ guid: role.definitionGuid, oldSort: role.sortOrder, newSort: idx }))
        .filter(({ oldSort, newSort }) => oldSort !== newSort);
      if (updates.length === 0) return;
      try {
        for (const u of updates) {
          await setRoleDefinitionSortOrder(u.guid, u.newSort);
        }
        // Single refresh after all PUTs settle — avoids N flicker passes.
        await refreshBundleRoles();
      } catch (err) {
        setError((err as any)?.message || "Failed to save reorder.");
        // Refresh anyway to pick up whatever partial state landed; user
        // sees the current truth and can retry the drag.
        await refreshBundleRoles();
      }
    },
    [refreshBundleRoles],
  );

  // ── Roles fetch on selection change ───────────────────────

  const selectedBundle = useMemo<RoleBundleResponse | null>(() => {
    if (!selectedGuid) return null;
    return bundles.find((b) => b.roleBundleGuid === selectedGuid) ?? null;
  }, [bundles, selectedGuid]);

  // iter 4 — Retire / Reactivate the currently-selected bundle. Selection
  // is intentionally preserved across both flows so the user can see the
  // bundle they just toggled in the right pane (the left list filter
  // may hide it when retired and Show retired is off — that's expected).
  // MUST be declared after selectedBundle to avoid a temporal dead zone in
  // the useCallback dep arrays (same hoisting class as handleDetachRole).
  const handleRetireBundle = useCallback(async () => {
    if (!selectedBundle) return;
    try {
      await retireRoleBundle(selectedBundle.roleBundleGuid);
      await loadBundles();
    } catch (err) {
      setError((err as any)?.message || "Failed to retire bundle.");
    }
  }, [selectedBundle, loadBundles]);

  const handleReactivateBundle = useCallback(async () => {
    if (!selectedBundle) return;
    try {
      await updateRoleBundle(selectedBundle.roleBundleGuid, { isActive: true });
      await loadBundles();
    } catch (err) {
      setError((err as any)?.message || "Failed to reactivate bundle.");
    }
  }, [selectedBundle, loadBundles]);

  useEffect(() => {
    if (!selectedGuid) {
      setBundleRoles([]);
      return;
    }
    let cancelled = false;
    setRolesLoading(true);
    fetchRoleBundleRoles(selectedGuid)
      .then((roles) => {
        if (!cancelled) setBundleRoles(roles);
      })
      .catch(() => {
        if (!cancelled) setBundleRoles([]);
      })
      .finally(() => {
        if (!cancelled) setRolesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedGuid]);

  // ── Derived collections ───────────────────────────────────

  const filteredBundles = useMemo<RoleBundleResponse[]>(() => {
    const q = searchTerm.trim().toLowerCase();
    return bundles
      .filter((b) => showRetired || b.isActive)
      .filter((b) => {
        if (!q) return true;
        return (
          b.label.toLowerCase().includes(q)
          || b.bundleKey.toLowerCase().includes(q)
          || (b.description ?? "").toLowerCase().includes(q)
        );
      });
  }, [bundles, showRetired, searchTerm]);

  // ── Render ─────────────────────────────────────────────────

  return (
    <>
      {error && (
        <div className={styles.errorBanner}>
          {error}{" "}
          <button type="button" className={styles.linkBtn} onClick={loadBundles}>
            Retry
          </button>
        </div>
      )}

      <div className={styles.splitLayout}>
        {/* Left: bundle list */}
        <div className={styles.listColumn}>
          <div className={styles.listToolbar}>
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Search bundles"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="button"
              className={styles.addRoleBtn}
              onClick={() => setTrayMode("create")}
              title="Create a new Role Bundle"
            >
              + Add bundle
            </button>
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: "var(--text-secondary)",
              marginBlockEnd: 8,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
            />
            Show retired
          </label>

          {loading && bundles.length === 0 && (
            <div className={styles.empty}>Loading bundles…</div>
          )}

          {!loading && filteredBundles.length === 0 && (
            <div className={styles.empty}>
              {bundles.length === 0
                ? "No Role Bundles authored yet. Create the first one with + Add bundle (coming next iter)."
                : "No matches for that search."}
            </div>
          )}

          <div className={styles.roleList}>
            {filteredBundles.map((bundle) => (
              <BundleRow
                key={bundle.roleBundleGuid}
                bundle={bundle}
                selected={bundle.roleBundleGuid === selectedGuid}
                onClick={() => setSelectedGuid(bundle.roleBundleGuid)}
              />
            ))}
          </div>
        </div>

        {/* Right: bundle detail */}
        <div className={styles.detailColumn}>
          {!selectedBundle ? (
            <div className={styles.empty}>
              Select a Role Bundle from the list to view its detail and roles.
            </div>
          ) : (
            <BundleDetail
              bundle={selectedBundle}
              roles={bundleRoles}
              rolesLoading={rolesLoading}
              onEditClick={() => setTrayMode("edit")}
              onAddRolesClick={() => setAddRolesTrayOpen(true)}
              onDetachRole={handleDetachRole}
              onReorderRoles={handleReorderRoles}
              onRetire={handleRetireBundle}
              onReactivate={handleReactivateBundle}
            />
          )}
        </div>
      </div>

      {/* iter 2 — Create / Edit tray */}
      {trayMode && (
        <BundleEditTray
          mode={trayMode}
          initialBundle={trayMode === "edit" ? (selectedBundle ?? undefined) : undefined}
          open={true}
          onClose={() => setTrayMode(null)}
          onSaved={async (savedGuid) => {
            // Refresh the list so the new/updated row reflects in the
            // left pane, then select the saved bundle so the right pane
            // re-renders with the new values.
            await loadBundles();
            setSelectedGuid(savedGuid);
          }}
        />
      )}

      {/* iter 3 — Add roles to bundle picker */}
      {addRolesTrayOpen && selectedBundle && (
        <AddRoleToBundleTray
          bundle={selectedBundle}
          open={true}
          onClose={() => setAddRolesTrayOpen(false)}
          onAttached={async () => {
            // Refresh both the bundle list (RoleCount) AND the per-bundle
            // role list so the new roles appear immediately.
            await loadBundles();
            await refreshBundleRoles();
          }}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────
// Subcomponents
// ────────────────────────────────────────────────────────────

function BundleRow({
  bundle,
  selected,
  onClick,
}: {
  bundle: RoleBundleResponse;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={
        selected ? `${styles.roleRow} ${styles.roleRowActive}` : styles.roleRow
      }
      onClick={onClick}
    >
      <div className={styles.roleRowMain}>
        <span
          className={styles.roleRowLabel}
          style={{ opacity: bundle.isActive ? 1 : 0.55 }}
        >
          {bundle.label}
        </span>
        <div className={styles.roleRowMeta}>
          <span
            className={styles.tagRhythm}
            title={`${bundle.roleCount} role(s) in this bundle`}
          >
            {bundle.roleCount} role{bundle.roleCount === 1 ? "" : "s"}
          </span>
          {!bundle.isActive && (
            <span
              className={styles.tagLocked}
              title="This bundle is retired"
              style={{ background: "var(--bg-elevated)" }}
            >
              Retired
            </span>
          )}
          {bundle.audienceEntityGuids.length > 0 && (
            <span
              className={styles.tagLocked}
              title="Audience-restricted (visible only to listed entities + descendants)"
            >
              Audience
            </span>
          )}
        </div>
      </div>
      <div
        className={styles.roleRowDesc}
        style={{ fontFamily: "monospace", fontSize: 11 }}
      >
        {bundle.bundleKey}
      </div>
      {bundle.description && (
        <div className={styles.roleRowDesc}>{bundle.description}</div>
      )}
    </button>
  );
}

function BundleDetail({
  bundle,
  roles,
  rolesLoading,
  onEditClick,
  onAddRolesClick,
  onDetachRole,
  onReorderRoles,
  onRetire,
  onReactivate,
}: {
  bundle: RoleBundleResponse;
  roles: RoleBundleRoleSummaryResponse[];
  rolesLoading: boolean;
  onEditClick: () => void;
  onAddRolesClick: () => void;
  onDetachRole: (definitionGuid: string) => void;
  onReorderRoles: (newOrder: RoleBundleRoleSummaryResponse[]) => Promise<void>;
  onRetire: () => Promise<void>;
  onReactivate: () => Promise<void>;
}) {
  // iter 4 — Retire confirm-strip state. Inline pattern mirrors the
  // "Retire role" / "Detach rhythm" inline confirms in AdminRoleRhythms.
  // Reactivate doesn't get a confirm step — it's the inverse of a
  // potentially-accidental retire, and zero-friction undo is the right
  // call.
  const [confirmingRetire, setConfirmingRetire] = React.useState(false);
  const [retiring, setRetiring] = React.useState(false);
  const [reactivating, setReactivating] = React.useState(false);

  // Reset confirm state when the selected bundle changes (clicking a
  // different row in the list while the confirm strip is open shouldn't
  // leave it hanging on the next bundle).
  React.useEffect(() => {
    setConfirmingRetire(false);
  }, [bundle.roleBundleGuid]);

  const handleRetireClick = async () => {
    setRetiring(true);
    try {
      await onRetire();
      setConfirmingRetire(false);
    } finally {
      setRetiring(false);
    }
  };

  const handleReactivateClick = async () => {
    setReactivating(true);
    try {
      await onReactivate();
    } finally {
      setReactivating(false);
    }
  };
  // iter 3.5 — drag-to-reorder local state. Mirrors AdminRoleRhythms
  // TaskRow pattern: drag start sets draggedGuid, drag over sets the
  // dragOverGuid for visual feedback, drop computes the new order and
  // hands it to the parent's onReorderRoles. Native HTML5 DnD; no lib.
  const [draggedGuid, setDraggedGuid] = React.useState<string | null>(null);
  const [dragOverGuid, setDragOverGuid] = React.useState<string | null>(null);
  const [reordering, setReordering] = React.useState(false);

  const handleDrop = async (targetGuid: string, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverGuid(null);
    setDraggedGuid(null);

    const sourceGuid = e.dataTransfer.getData("text/plain");
    if (!sourceGuid || sourceGuid === targetGuid) return;

    const sourceIdx = roles.findIndex((r) => r.definitionGuid === sourceGuid);
    const targetIdx = roles.findIndex((r) => r.definitionGuid === targetGuid);
    if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) return;

    const reordered = [...roles];
    const [moved] = reordered.splice(sourceIdx, 1);
    reordered.splice(targetIdx, 0, moved);

    setReordering(true);
    try {
      await onReorderRoles(reordered);
    } finally {
      setReordering(false);
    }
  };
  return (
    <div className={styles.detailPanel}>
      <div className={styles.detailHeader}>
        <h2 className={styles.detailTitle}>{bundle.label}</h2>
        <div className={styles.detailMeta}>
          <span className={styles.detailMetaTag} style={{ fontFamily: "monospace" }}>
            {bundle.bundleKey}
          </span>
          {!bundle.isActive && (
            <span className={styles.tagLocked} title="Retired bundle">
              Retired
            </span>
          )}
          {bundle.isSystemLocked && (
            <span className={styles.tagLocked} title="Platform-locked">
              🔒 Platform
            </span>
          )}
          {/* iter 2 — Edit bundle button. Mirrors the Clone/Retire pattern
              on RoleDetail in AdminRoleRhythms. */}
          <button
            type="button"
            className={styles.cloneRoleBtn}
            onClick={onEditClick}
            title="Edit this Role Bundle"
          >
            Edit bundle
          </button>
          {/* iter 4 — Retire / Reactivate. Symmetric: active bundles
              get a confirm-required Retire affordance; retired bundles
              get a zero-friction Reactivate (undo). */}
          {bundle.isActive ? (
            <button
              type="button"
              className={styles.retireRoleBtn}
              onClick={() => setConfirmingRetire(true)}
              disabled={retiring || confirmingRetire}
              title="Retire this Role Bundle"
            >
              Retire bundle
            </button>
          ) : (
            <button
              type="button"
              className={styles.cloneRoleBtn}
              onClick={() => void handleReactivateClick()}
              disabled={reactivating}
              title="Reactivate this Role Bundle"
            >
              {reactivating ? "Reactivating…" : "Reactivate"}
            </button>
          )}
        </div>
      </div>

      {bundle.description && (
        <p className={styles.detailDesc}>{bundle.description}</p>
      )}

      {/* iter 4 — inline retire confirm strip. Mirrors the "Retire role"
          and "Detach rhythm" inline confirms in AdminRoleRhythms. */}
      {confirmingRetire && (
        <div className={styles.retireRoleConfirm}>
          <p className={styles.retireRoleConfirmText}>
            <strong>Retire {bundle.label}?</strong>{" "}
            The bundle is hidden from new community pickers and the
            "+ Add bundle" suggestion list. Communities that have already
            applied this bundle keep their roles — the existing effective
            view is unaffected per POLICY_ROLE_BUNDLES §7.3. You can
            reactivate later via the bundle's detail panel (toggle
            "Show retired" in the left list to find retired bundles).
          </p>
          <div className={styles.retireRoleConfirmActions}>
            <button
              type="button"
              className={styles.retireRoleCancelBtn}
              onClick={() => setConfirmingRetire(false)}
              disabled={retiring}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.retireRoleConfirmBtn}
              onClick={() => void handleRetireClick()}
              disabled={retiring}
            >
              {retiring ? "Retiring…" : "Retire bundle"}
            </button>
          </div>
        </div>
      )}

      <FieldGrid bundle={bundle} />

      <section
        className={styles.taskSection}
        style={{ marginBlockStart: 16 }}
      >
        <div
          className={styles.taskSectionHeader}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <span>
            Roles in this bundle
            <span className={styles.taskSectionCount}>{roles.length}</span>
          </span>
          {/* iter 3 — Add roles affordance. Disabled on retired bundles
              (can't author into a bundle that's hidden from new pickers). */}
          <button
            type="button"
            className={styles.addRoleBtn}
            onClick={onAddRolesClick}
            disabled={!bundle.isActive}
            title={
              bundle.isActive
                ? "Pick roles from the catalog and attach them to this bundle"
                : "Retired bundles can't accept new roles"
            }
            style={{ fontSize: 12, padding: "3px 10px" }}
          >
            + Add roles
          </button>
        </div>
        {rolesLoading && roles.length === 0 && (
          <div className={styles.empty}>Loading roles…</div>
        )}
        {!rolesLoading && roles.length === 0 && (
          <div className={styles.empty}>
            No roles in this bundle yet. Click <strong>+ Add roles</strong>{" "}
            to pick from the catalog, or open a role on a pillar tab and
            use the <strong>Bundle</strong> picker on its detail panel.
          </div>
        )}
        {roles.map((role) => {
          const isDragged = draggedGuid === role.definitionGuid;
          const isDragOver = dragOverGuid === role.definitionGuid && !isDragged;
          return (
            <div
              key={role.definitionGuid}
              className={styles.taskRow}
              draggable={!reordering}
              onDragStart={(e) => {
                setDraggedGuid(role.definitionGuid);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", role.definitionGuid);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (dragOverGuid !== role.definitionGuid) {
                  setDragOverGuid(role.definitionGuid);
                }
              }}
              onDragLeave={() => setDragOverGuid(null)}
              onDragEnd={() => {
                setDraggedGuid(null);
                setDragOverGuid(null);
              }}
              onDrop={(e) => handleDrop(role.definitionGuid, e)}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 6,
                opacity: isDragged ? 0.4 : 1,
                borderTop: isDragOver ? "2px solid var(--accent-primary)" : undefined,
                transition: "opacity 0.1s, border 0.1s",
              }}
            >
              {/* iter 3.5 — drag handle. Visual affordance; the row
                  itself carries the draggable={true} attribute so the
                  user can grab anywhere on the row. */}
              <span
                aria-hidden="true"
                title="Drag to reorder"
                style={{
                  color: "var(--text-muted)",
                  cursor: reordering ? "wait" : "grab",
                  fontSize: 14,
                  paddingInlineStart: 4,
                  paddingBlockStart: 2,
                  userSelect: "none",
                  letterSpacing: -1,
                }}
              >
                ⋮⋮
              </span>

              <span
                className={styles.taskRowClickArea}
                style={{ cursor: "default", flex: 1, minWidth: 0 }}
              >
                <span className={styles.taskName}>{role.label}</span>
                <span className={styles.taskMeta}>
                  {role.roleCategory} · {pillarShortLabel(role.pillarType)}
                  {role.hasRhythm ? " · rhythm" : ""}
                </span>
              </span>
              {/* iter 3 — Detach this role from the bundle. The role
                  itself isn't deleted; its role_bundle_guid is just
                  cleared, so it returns to the universal core / per-org
                  custom pool. */}
              <button
                type="button"
                onClick={() => {
                  if (confirm(`Detach "${role.label}" from "${bundle.label}"?\n\nThe role itself is unchanged — it just leaves this bundle.`)) {
                    onDetachRole(role.definitionGuid);
                  }
                }}
                disabled={reordering}
                title="Detach role from this bundle"
                aria-label={`Detach ${role.label} from bundle`}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--text-muted)",
                  cursor: reordering ? "wait" : "pointer",
                  fontSize: 16,
                  lineHeight: 1,
                  padding: "2px 6px",
                  alignSelf: "center",
                }}
              >
                ×
              </button>
            </div>
          );
        })}
      </section>
    </div>
  );
}

function FieldGrid({ bundle }: { bundle: RoleBundleResponse }) {
  const rows: Array<{ label: string; render: () => React.ReactNode }> = [
    {
      label: "Applicable entity types",
      render: () =>
        bundle.applicableEntityTypes.length === 0
          ? <Muted>Any entity_type</Muted>
          : <ChipRow items={bundle.applicableEntityTypes} />,
    },
    {
      label: "Audience",
      render: () =>
        bundle.audienceEntityGuids.length === 0
          ? <Muted>Platform-wide (no audience restriction)</Muted>
          : (
            <Muted>
              {bundle.audienceEntityGuids.length} entity guid
              {bundle.audienceEntityGuids.length === 1 ? "" : "s"}
              {" "}(restricted to listed entities + descendants)
            </Muted>
          ),
    },
    {
      label: "Applicable project categories",
      render: () =>
        bundle.applicableProjectCategories.length === 0
          ? <Muted>All categories</Muted>
          : <ChipRow items={bundle.applicableProjectCategories} />,
    },
    {
      label: "Created",
      render: () =>
        <Muted>{new Date(bundle.createdAt).toLocaleString()}</Muted>,
    },
    {
      label: "Last updated",
      render: () =>
        <Muted>{new Date(bundle.updatedAt).toLocaleString()}</Muted>,
    },
  ];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "max-content 1fr",
        gap: "6px 16px",
        marginBlockStart: 12,
        fontSize: 13,
      }}
    >
      {rows.map((row) => (
        <React.Fragment key={row.label}>
          <div
            style={{
              color: "var(--text-secondary)",
              fontWeight: 500,
              paddingBlockStart: 2,
            }}
          >
            {row.label}
          </div>
          <div>{row.render()}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>
      {children}
    </span>
  );
}

function ChipRow({ items }: { items: string[] }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {items.map((item) => (
        <span
          key={item}
          style={{
            display: "inline-block",
            padding: "2px 8px",
            background: "var(--bg-elevated)",
            color: "var(--text-primary)",
            borderRadius: 12,
            fontSize: 12,
            fontFamily: "monospace",
          }}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function pillarShortLabel(pillarType: string): string {
  switch (pillarType) {
    case "geo_community":  return "Geo";
    case "org_community":  return "Org / Enabler";
    case "full_project":   return "Project";
    case "workspace":      return "Workspace";
    case "solution":       return "Solution";
    default:               return pillarType;
  }
}
